import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useModal } from 'mui-modal-provider';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import ApEditorMode from '../../../enums/ApEditorMode';
import useActionMessageDataMapper from '../../../hooks/useActionMessageDataMapper';
import useAsyncDeleteApPlanAction from '../../../hooks/useAsyncDeleteApPlanAction';
import useAsyncDeleteApTemplateAction from '../../../hooks/useAsyncDeleteApTemplateAction';
import { ApActionType } from '../../../sic-lib/enums/ApActionType';
import { ApEditorActions } from '../../../sic-lib/reducers/actionPlanEditorReducer';
import { ApActionBase } from '../../../sic-lib/types/ActionPlan';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';
import DisplayHTML from '../../Shared/DisplayHTML/DisplayHTML';
import ActionReorderButtons from '../ActionReorderButtons/ActionReorderButtons';

const useStyles = makeStyles()((theme: Theme) => ({
    flexContainer: {
        padding: theme.spacing(1),
    },
    iconButton: {
        [theme.breakpoints.up(0)]: {
            padding: '3px',
        },
        [theme.breakpoints.up('lg')]: {
            padding: theme.spacing(1),
        },
    },
    messageWrapper: {
        padding: theme.spacing(2, 1),
    },
}));

type Props = {
    action: ApActionBase;
    actionIndex: number;
    hasNextAction: boolean;
    hasPrevAction: boolean;
    apRootId: number;
    apEditMode: ApEditorMode;
    apDispatch: React.Dispatch<ApEditorActions>;
};

function ApEditorAction({
    action,
    actionIndex,
    hasNextAction,
    hasPrevAction,
    apRootId,
    apEditMode,
    apDispatch,
}: Props) {
    const { classes } = useStyles();
    const { showModal } = useModal();
    const { asyncDeleteApTemplateAction } = useAsyncDeleteApTemplateAction();
    const { asyncDeleteApPlanAction } = useAsyncDeleteApPlanAction();

    const messageData = useActionMessageDataMapper(apEditMode, action);

    const handleEdit = () => {
        apDispatch({ type: 'SET_EDITOR_EDIT', actionIndex, setEdit: true });
    };

    const handleNavigate = () => {
        apDispatch({
            type: 'SET_EDITOR_LOCATION_FORWARD',
            nextLocation: {
                title: action.title,
                actionIndex,
            },
        });
    };

    const handleDelete = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Confirm Delete Action',
            description: 'This message will permanently be removed',
            onConfirm: async () => {
                if (apEditMode === ApEditorMode.template) {
                    await asyncDeleteApTemplateAction(apRootId, action.actionId);
                } else {
                    await asyncDeleteApPlanAction(apRootId, action.actionId);
                }

                apDispatch({
                    type: 'REMOVE_EDITOR_ACTION',
                    actionIndex,
                });
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };

    return (
        <Paper elevation={0} variant="outlined">
            <Grid
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                spacing={2}
                className={classes.flexContainer}
            >
                <Grid item xs={true}>
                    <Typography variant="subtitle1">{action.title}</Typography>
                </Grid>
                <Grid item xs="auto">
                    <Tooltip title="Edit">
                        <IconButton
                            className={classes.iconButton}
                            onClick={handleEdit}
                            size="large"
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item xs="auto">
                    <Tooltip title="Delete">
                        <IconButton
                            data-action-id={action.actionId}
                            data-action-index={actionIndex}
                            className={classes.iconButton}
                            onClick={handleDelete}
                            size="large"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            <Divider />
            <div className={classes.messageWrapper}>
                {action.type === ApActionType.email && (
                    <React.Fragment>
                        <Typography variant="subtitle2" gutterBottom>
                            {`Subject: ${messageData.subject}`}
                        </Typography>
                        <DisplayHTML htmlString={messageData.body} />
                    </React.Fragment>
                )}
                {action.type !== ApActionType.email && (
                    <Typography variant="body1">{messageData.body}</Typography>
                )}
            </div>
            <Grid
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                spacing={2}
                className={classes.flexContainer}
            >
                <Grid item xs={true}>
                    <Button
                        size="small"
                        onClick={handleNavigate}
                        startIcon={!action.actions.length ? <AddIcon /> : null}
                    >
                        {`Child actions ${
                            action.actions.length ? '(' + action.actions.length + ')' : ''
                        }`}
                    </Button>
                </Grid>
                {
                    /* no API available for re-arranging actions of ApPlan, only ApTemplate */
                    apEditMode === ApEditorMode.template && (
                        <Grid item xs="auto">
                            <ActionReorderButtons
                                apRootId={apRootId}
                                apActionId={action.actionId}
                                apEditMode={apEditMode}
                                apActionIndex={actionIndex}
                                apDispatch={apDispatch}
                                hasPrevAction={hasPrevAction}
                                hasNextAction={hasNextAction}
                            />
                        </Grid>
                    )
                }
            </Grid>
        </Paper>
    );
}

export default ApEditorAction;
